import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Wallet, TrendingUp, Receipt, Info, Landmark } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { CURRENCY_SYMBOL } from "@/data/categories";
import { fmt } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const CONNECT = {
  en: { title: "Bank payouts (Stripe)", desc: "Connect your bank securely with Stripe to receive real payouts on your schedule.", btn: "Connect payouts", manage: "Manage / update", on: "Payouts active", off: "Not connected" },
  nl: { title: "Bankuitbetalingen (Stripe)", desc: "Koppel je bank veilig via Stripe om echte uitbetalingen te ontvangen.", btn: "Uitbetalingen koppelen", manage: "Beheren / bijwerken", on: "Uitbetalingen actief", off: "Niet gekoppeld" },
  pl: { title: "Wypłaty bankowe (Stripe)", desc: "Połącz bank bezpiecznie przez Stripe, aby otrzymywać realne wypłaty.", btn: "Połącz wypłaty", manage: "Zarządzaj / aktualizuj", on: "Wypłaty aktywne", off: "Niepołączone" },
  it: { title: "Pagamenti bancari (Stripe)", desc: "Collega la tua banca in modo sicuro con Stripe per ricevere pagamenti reali.", btn: "Collega i pagamenti", manage: "Gestisci / aggiorna", on: "Pagamenti attivi", off: "Non collegato" },
  sk: { title: "Bankové výplaty (Stripe)", desc: "Bezpečne prepojte banku cez Stripe a dostávajte reálne výplaty.", btn: "Prepojiť výplaty", manage: "Spravovať / upraviť", on: "Výplaty aktívne", off: "Neprepojené" },
  es: { title: "Pagos bancarios (Stripe)", desc: "Conecta tu banco de forma segura con Stripe para recibir pagos reales.", btn: "Conectar pagos", manage: "Gestionar / actualizar", on: "Pagos activos", off: "No conectado" },
  pt: { title: "Pagamentos bancários (Stripe)", desc: "Ligue o seu banco em segurança com a Stripe para receber pagamentos reais.", btn: "Ligar pagamentos", manage: "Gerir / atualizar", on: "Pagamentos ativos", off: "Não ligado" },
};

export const PayoutPanel = () => {
  const { t, tc, lang } = useLang();
  const c = CONNECT[lang] || CONNECT.en;
  const [summary, setSummary] = useState(null);
  const [conn, setConn] = useState(null);
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    bank_country: "",
    iban: "",
    bank_name: "",
    schedule: "weekly",
  });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [s, acc, cs] = await Promise.all([
      api.get("/payout/summary").then((r) => r.data).catch(() => null),
      api.get("/payout/account").then((r) => r.data).catch(() => null),
      api.get("/connect/status").then((r) => r.data).catch(() => null),
    ]);
    setSummary(s);
    setConn(cs);
    if (acc) setForm((f) => ({ ...f, ...acc }));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onboard = async () => {
    try {
      const { data } = await api.post("/connect/onboard");
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/payout/account", {
        first_name: form.first_name,
        last_name: form.last_name,
        bank_country: form.bank_country,
        iban: form.iban,
        bank_name: form.bank_name,
        schedule: form.schedule,
      });
      toast.success(t("pd_saved"));
      load();
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const sym = CURRENCY_SYMBOL[summary?.currency] || "€";
  const inputCls = "mt-1.5 rounded-xl border-ppc bg-ppc-bg";

  const Stat = ({ icon: Icon, label, value, accent }) => (
    <div className="glass-card rounded-3xl p-5" data-testid={`payout-stat-${label}`}>
      <span
        className="grid h-10 w-10 place-items-center rounded-2xl border border-ppc bg-ppc-bg"
      >
        <Icon className="h-5 w-5" style={{ color: accent || "#9184d9" }} />
      </span>
      <p className="mt-3 text-xs uppercase tracking-wider text-ppc-muted">{label}</p>
      <p className="font-display mt-1 text-2xl font-medium text-ppc-text">{value}</p>
    </div>
  );

  return (
    <div className="space-y-8">
      <h3 className="font-display text-xl font-medium text-ppc-text">{t("payout_heading")}</h3>

      {/* Stripe Connect */}
      <div className="glass-card rounded-3xl p-6" data-testid="connect-card">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="max-w-md">
            <h4 className="font-display font-medium text-ppc-text">{c.title}</h4>
            <p className="mt-1 text-sm text-ppc-muted">{c.desc}</p>
          </div>
          <span
            data-testid="connect-status-badge"
            className={`rounded-xl px-3 py-1 text-xs font-medium ${
              conn?.payouts_enabled
                ? "st-paid"
                : "st-pending"
            }`}
          >
            {conn?.payouts_enabled ? c.on : c.off}
          </span>
        </div>
        <Button
          data-testid="connect-btn"
          onClick={onboard}
          className="mt-4 rounded-xl btn-primary"
        >
          {conn?.connected ? c.manage : c.btn}
        </Button>
      </div>

      {/* Earnings stats */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat icon={Wallet} label={t("total_earned")} value={fmt(sym, summary?.total_earned || 0)} accent="#9184d9" />
        <Stat icon={TrendingUp} label={t("total_gross")} value={fmt(sym, summary?.total_gross || 0)} accent="#c6bff0" />
        <Stat icon={Receipt} label={t("total_fees")} value={fmt(sym, summary?.total_fees || 0)} accent="#9184d9" />
        <Stat icon={Landmark} label={t("paid_jobs")} value={summary?.count || 0} accent="#c6bff0" />
      </div>

      {/* Recent earnings */}
      {summary?.recent?.length > 0 && (
        <div className="glass-card rounded-3xl p-6">
          <h4 className="font-display font-medium text-ppc-text">{t("recent_earnings")}</h4>
          <div className="mt-4 space-y-2">
            {summary.recent.map((b) => (
              <div
                key={b.id}
                data-testid={`earning-row-${b.id}`}
                className="flex items-center justify-between rounded-xl bg-ppc-bg px-4 py-2.5 text-sm"
              >
                <div>
                  <span className="font-medium text-ppc-text">{b.listing_title}</span>
                  <span className="ml-2 text-xs text-ppc-muted">{b.date}</span>
                </div>
                <div className="text-right">
                  <span className="font-display font-medium text-primary-green">
                    {t("you_earn")} {fmt(sym, b.provider_earning ?? b.amount)}
                  </span>
                  <span className="ml-2 text-xs text-ppc-muted">
                    ({t("fee_label")} {fmt(sym, b.booking_fee ?? b.service_fee ?? 0)})
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Payout setup */}
      <form onSubmit={save} className="glass-card rounded-3xl p-6">
        <h4 className="font-display font-medium text-ppc-text">{t("payout_details")}</h4>
        {!summary?.has_account && (
          <p className="mt-2 text-sm text-ppc-muted">{t("setup_payout_first")}</p>
        )}
        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          <div>
            <Label>{t("pd_first")}</Label>
            <Input data-testid="payout-first-input" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} required className={inputCls} />
          </div>
          <div>
            <Label>{t("pd_last")}</Label>
            <Input data-testid="payout-last-input" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} required className={inputCls} />
          </div>
          <div>
            <Label>{t("pd_country")}</Label>
            <Input data-testid="payout-country-input" value={form.bank_country} onChange={(e) => set("bank_country", e.target.value)} required className={inputCls} />
          </div>
          <div>
            <Label>{t("pd_bank")}</Label>
            <Input data-testid="payout-bank-input" value={form.bank_name} onChange={(e) => set("bank_name", e.target.value)} required className={inputCls} />
          </div>
          <div className="sm:col-span-2">
            <Label>{t("pd_iban")}</Label>
            <Input data-testid="payout-iban-input" value={form.iban} onChange={(e) => set("iban", e.target.value)} required className={inputCls} placeholder="NL00 BANK 0000 0000 00" />
          </div>
          <div className="sm:col-span-2">
            <Label>{t("payout_schedule")}</Label>
            <Select value={form.schedule} onValueChange={(v) => set("schedule", v)}>
              <SelectTrigger data-testid="payout-schedule-select" className={inputCls}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="">
                <SelectItem value="weekly">{t("sched_weekly")}</SelectItem>
                <SelectItem value="monthly">{t("sched_monthly")}</SelectItem>
                <SelectItem value="instant">{t("sched_instant")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <Button type="submit" disabled={busy} data-testid="payout-save-btn" className="mt-5 rounded-xl btn-primary">
          {t("pd_save")}
        </Button>

        <div className="mt-5 flex items-start gap-2 rounded-2xl bg-ppc-bg p-4 text-xs leading-relaxed text-ppc-muted" data-testid="payout-note">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{t("payout_note")}</span>
        </div>
      </form>
    </div>
  );
};
